import React, { FunctionComponent, useState, useEffect } from "react"
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Checkbox,
    FormControl,
    Tooltip
} from "@material-ui/core"
import { useMutation } from "@apollo/client"
import { useNotification } from "../../common/hooks/useNotification"
import { Prompt, PromptSortFields, Sort } from "../../../typings/types"
import { PromptRow } from "./PromptRow"
import { DELETE_PROMPT } from "../../mutations/DELETE_PROMPT"
import { PromptsTableToolbar } from "./PromptsTableToolbar"
import { SortDirections, toggleSortDirection } from "../../common/SortUtilities"
import styles from "./PromptsTable.module.scss"
import { usePromptsTableContext } from "./hooks/usePromptsTableContext"
import { useSelectedPrompts } from "./hooks/useSelectedPrompts"

enum SortFields {
    NAME = "name",
    LANGUAGE = "language",
    FORMAT = "format",
    EXTENSION = "extension",
    USER = "user",
    CREATED_AT = "createdAt",
    UPDATED_AT = "updatedAt",
    MODIFIED_BY = "modifiedBy"
}

interface PromptsTableProps {
    prompts: Prompt[]
    setActiveSort: React.Dispatch<React.SetStateAction<PromptSortFields>>
    refreshPrompts: () => void
    onBulkDownload: (promptUuids: string[]) => void
}

export const PromptsTable: FunctionComponent<PromptsTableProps> = (props: PromptsTableProps) => {
    const { prompts, setActiveSort, refreshPrompts, onBulkDownload } = props
    const [activeSortField, setActiveSortField] = useState<SortFields>(SortFields.NAME)
    const [activeSortDirection, setActiveSortDirection] = useState<SortDirections>(
        SortDirections.ASC
    )
    const { notifySuccess, notifyError } = useNotification()
    const { selectAll, selectedPromptUuids } = usePromptsTableContext()

    const {
        removePrompt,
        isSelected,
        unselectAll,
        selectAllPrompts,
        deleteAll
    } = useSelectedPrompts()

    const [deletePrompt] = useMutation<{ deletePrompt: Boolean }>(DELETE_PROMPT, {
        onCompleted: data => {
            if (data.deletePrompt) {
                notifySuccess("Deletion Successful")
                refreshPrompts()
            }
        },
        onError: err => notifyError("Deletion Failed")
    })

    const toggleSort = (field: SortFields): void => {
        if (activeSortField !== field) setActiveSortField(field)
        else toggleSortDirection(setActiveSortDirection, activeSortDirection)
    }

    const handleDownload = () => {
        onBulkDownload(selectedPromptUuids)
        unselectAll()
    }

    const onPromptDelete = (uuid: string) => {
        deletePrompt({
            variables: {
                mediaUUID: uuid
            }
        })
        if (isSelected(uuid)) {
            removePrompt(uuid)
        }
    }

    const toggleSelectAll = React.useCallback(() => {
        if (!selectAll) selectAllPrompts([...prompts.map(prompt => prompt.audioLocation)])
        if (selectAll) unselectAll()
    }, [selectAll, selectAllPrompts, unselectAll, prompts])

    useEffect(() => {
        // See TTPSP-404 for removing type casting here
        const newActiveSort = {} as PromptSortFields
        const newActiveSortDirection = (activeSortDirection as unknown) as Sort
        newActiveSort[activeSortField] = newActiveSortDirection
        setActiveSort(newActiveSort)
    }, [activeSortField, activeSortDirection])

    return (
        <div className={styles.PromptsTable}>
            <PromptsTableToolbar
                numSelected={selectedPromptUuids.length}
                onDownload={handleDownload}
                refreshTable={refreshPrompts}
                onDelete={deleteAll}
            />
            <TableContainer>
                <Table data-testid="promptsTable">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <FormControl>
                                    <Tooltip
                                        title={selectAll ? "Unselect All" : "Select All"}
                                        arrow
                                        placement="top"
                                    >
                                        <Checkbox
                                            checked={selectAll}
                                            onChange={toggleSelectAll}
                                            className={"prompt-select-all-box"}
                                            inputProps={{ id: "select_all" }}
                                        />
                                    </Tooltip>
                                </FormControl>
                            </TableCell>
                            <TableCell />
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.NAME}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.NAME)}
                                >
                                    Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.LANGUAGE}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.LANGUAGE)}
                                >
                                    Language
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.FORMAT}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.FORMAT)}
                                >
                                    Format
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.EXTENSION}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.EXTENSION)}
                                >
                                    Extension
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.USER}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.USER)}
                                >
                                    Author
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.CREATED_AT}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.CREATED_AT)}
                                >
                                    Created (UTC)
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.UPDATED_AT}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.UPDATED_AT)}
                                >
                                    Last Edited (UTC)
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={activeSortField === SortFields.MODIFIED_BY}
                                    direction={activeSortDirection}
                                    onClick={() => toggleSort(SortFields.MODIFIED_BY)}
                                >
                                    Last Modified
                                </TableSortLabel>
                            </TableCell>
                            {/* edit cell */}
                            <TableCell />
                            {/* delete cell */}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prompts.map((prompt, i) => (
                            <PromptRow
                                key={i.toString()}
                                prompt={prompt}
                                rowIndex={i}
                                handleDelete={() => onPromptDelete(prompt.audioLocation)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}
